import { motion } from "framer-motion";
import { Radio, MapPin, Sparkles } from "lucide-react";

import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const RADIOS = [
  {
    name: "Fun Radio",
    country: "France",
    flag: "🇫🇷",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/sqh6a6zc_fun%20radio%20.png",
    support: "Featured in the “Nouveautés” selection",
    accent: true,
  },
  {
    name: "M40 Radio",
    country: "Paris, France",
    flag: "🇫🇷",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/tud9nv1c_M40.jpg",
    support: "Radio Support",
  },
  {
    name: "Alta Frequenza",
    country: "Corsica",
    flag: "🇫🇷",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/7le7qjrf_images.png",
    support: "Radio Support",
  },
  {
    name: "Puls FM",
    country: "Sweden",
    flag: "🇸🇪",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/jwaw8b4z_PulsFM.jpeg",
    support: "Radio Support",
  },
  {
    name: "THE BEAT",
    country: "Sweden",
    flag: "🇸🇪",
    logo: null,
    support: "Radio Support",
  },
  {
    name: "OURO Verde FM",
    country: "Brazil",
    flag: "🇧🇷",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/7uh8kq9r_Ouro%20verde%20brasil%20.jpeg",
    support: "Radio Support",
  },
  {
    name: "Frecuencia Millennial",
    country: "Mexico",
    flag: "🇲🇽",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/oi8cvwiy_frqeunce.jpeg",
    support: "Radio Support",
  },
  {
    name: "Caribe 93.5 FM",
    country: "Venezuela",
    flag: "🇻🇪",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/2rxbikfg_images%20%281%29.jpeg",
    support: "Radio Support",
  },
  {
    name: "Miami Beach Radio",
    country: "United States",
    flag: "🇺🇸",
    logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_irwan-craw-music/artifacts/pbugkie4_images.jpeg",
    support: "Radio Support",
  },
];

// Deterministic gold particles (mounted once)
const PARTICLES = [
  { x: 6, y: 22, s: 1.6, d: 0 },
  { x: 18, y: 74, s: 1.2, d: 2.1 },
  { x: 32, y: 30, s: 2, d: 1.4 },
  { x: 48, y: 66, s: 1.4, d: 3.2 },
  { x: 64, y: 20, s: 1.8, d: 4.1 },
  { x: 78, y: 80, s: 2, d: 0.8 },
  { x: 92, y: 40, s: 1.4, d: 2.6 },
];

export const RadioSupport = () => {
  return (
    <section
      id="radio-support"
      data-testid="radio-support-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)] overflow-hidden"
    >
      {/* Ambient gold gradients */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 60% 40% at 15% 10%, rgba(201,169,97,0.10), transparent 60%), radial-gradient(ellipse 50% 40% at 90% 95%, rgba(201,169,97,0.10), transparent 60%)",
        }}
      />

      {/* Floating particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {PARTICLES.map((p, i) => (
          <motion.span
            key={i}
            aria-hidden
            initial={{ opacity: 0, y: 0 }}
            animate={{
              opacity: [0, 0.7, 0.3, 0.7, 0],
              y: [0, -30, -60, -100, -140],
            }}
            transition={{
              duration: 15 + (i % 4) * 2,
              delay: p.d,
              ease: "linear",
              repeat: Infinity,
            }}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.s}px`,
              height: `${p.s}px`,
              background:
                "radial-gradient(circle, rgba(229,204,148,0.9) 0%, rgba(201,169,97,0.4) 60%, rgba(201,169,97,0) 100%)",
              boxShadow: "0 0 6px rgba(229,204,148,0.6)",
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
        {/* Header */}
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Chapter 03 — Airplay
            </p>
            <h2
              data-testid="radio-support-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              Radio Support &{" "}
              <span className="font-editorial text-gold-hi">Airplay</span>
            </h2>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="max-w-md text-sm text-muted leading-relaxed"
          >
            Music supported by international radio stations across Europe,
            North America and South America.
          </motion.p>
        </motion.div>

        {/* Grid */}
        <motion.div
          variants={stagger(0.08, 0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5"
        >
          {RADIOS.map((r, i) => (
            <motion.article
              key={r.name}
              variants={fadeUp}
              data-testid={`radio-card-${i}`}
              className={`group relative overflow-hidden rounded-sm border ${
                r.accent
                  ? "border-[color:var(--gold)]/40 bg-gradient-to-br from-[color:var(--gold)]/[0.06] to-transparent"
                  : "border-[color:var(--line)] bg-bg-2"
              } p-6 md:p-7 backdrop-blur-md transition-all duration-700 hover:border-[color:var(--gold)]/60`}
            >
              {/* Corner index */}
              <span className="pointer-events-none absolute right-4 top-3 font-editorial text-base text-[color:var(--gold-hi)]/50">
                0{i + 1}
              </span>

              {/* Logo (if available) */}
              {r.logo ? (
                <div
                  data-testid={`radio-logo-${i}`}
                  className="mb-5 flex h-16 w-16 items-center justify-center overflow-hidden rounded-md bg-white/95 shadow-[0_0_20px_rgba(201,169,97,0.15)] ring-1 ring-[color:var(--gold)]/25 transition-transform duration-700 group-hover:scale-105"
                >
                  <img
                    src={r.logo}
                    alt={`${r.name} logo`}
                    className="h-full w-full object-contain p-1"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div
                  aria-hidden
                  className="mb-5 flex h-16 w-16 items-center justify-center rounded-md border border-[color:var(--line-strong)] bg-bg text-[color:var(--gold-hi)]/80"
                >
                  <Radio size={22} />
                </div>
              )}

              {/* Flag + radio icon */}
              <div className="flex items-center gap-3">
                <span
                  className="text-2xl leading-none"
                  aria-hidden
                  data-testid={`radio-flag-${i}`}
                >
                  {r.flag}
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--gold)]/40 text-gold transition-all duration-500 group-hover:border-[color:var(--gold)] group-hover:bg-[color:var(--gold)] group-hover:text-[#0a0a0a]">
                  <Radio size={12} />
                </span>
              </div>

              {/* Name */}
              <h3 className="mt-4 font-display text-xl md:text-2xl font-bold uppercase tracking-[0.05em] text-ink">
                {r.name}
              </h3>

              {/* Country */}
              <p
                data-testid={`radio-country-${i}`}
                className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.28em] text-muted"
              >
                <MapPin size={10} className="text-gold" />
                {r.country}
              </p>

              {/* Divider */}
              <div className="mt-5 h-px w-full bg-[color:var(--line)]" />

              {/* Support tag */}
              <div className="mt-4 flex items-start gap-2">
                {r.accent ? (
                  <Sparkles size={12} className="mt-0.5 shrink-0 text-gold" />
                ) : (
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[color:var(--gold)]" />
                )}
                <p
                  className={
                    r.accent
                      ? "font-editorial text-base text-[color:var(--gold-hi)]/95 leading-snug"
                      : "text-[11px] font-semibold uppercase tracking-[0.24em] text-ink/80"
                  }
                >
                  {r.support}
                </p>
              </div>

              {/* Hover glow */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(circle at 30% 15%, rgba(201,169,97,0.16), transparent 55%)",
                  boxShadow: "inset 0 0 60px rgba(201,169,97,0.05)",
                }}
              />
            </motion.article>
          ))}
        </motion.div>

        {/* Footer statement */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          data-testid="radio-support-footer"
          className="mt-14 md:mt-16 max-w-3xl mx-auto text-center font-editorial text-lg md:text-xl text-[color:var(--gold-hi)]/85"
        >
          International radio support helping bring Irwan Craw's music to
          audiences around the world.
        </motion.p>
      </div>
    </section>
  );
};
